/**
 * MuiTopMaterials - Optimized Top Materials Dashboard
 *
 * OPTIMIZATION: Phase 11.4 - Dashboard optimization
 *
 * Changes made:
 * - Replaced getProviders() with fetchTopMaterialsData() optimized function
 * - fetchTopMaterialsData fetches only providers with orders.content field
 * - Material aggregation happens in api_helper, reducing duplicate processing
 * - Added refresh button for manual data reload
 *
 * Benefits:
 * - Significantly reduced data transfer (no full provider data)
 * - Faster initial load - only fetches orders with content
 * - Pre-computed material rankings from API helper
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import StorefrontIcon from "@mui/icons-material/Storefront";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { fetchTopMaterialsData } from "../../helpers/api_helper";

const COLORS = [
  "#8b5cf6", "#06b6d4", "#f59e0b", "#ef4444", "#10b981",
  "#3b82f6", "#ec4899", "#84cc16", "#f97316", "#6366f1"
];

const MuiTopMaterials = () => {
  const { t } = useTranslation();

  document.title = t("Top Materials") + " | FlexFlow ERP";

  const [topMaterials, setTopMaterials] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("chart");

  // ============================================================================
  // OPTIMIZED DATA FETCHING
  // Uses fetchTopMaterialsData() which fetches only providers with orders.content
  // ============================================================================
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchTopMaterialsData();
      setAllMaterials(data.allMaterials);
      setTopMaterials(data.topMaterials);
    } catch (error) {
      console.error("Error fetching top materials data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Summary stats
  const stats = useMemo(() => {
    const totalMaterials = allMaterials.length;
    const totalQuantity = allMaterials.reduce((sum, p) => sum + p.quantity, 0);
    const topMaterial = topMaterials[0] || { name: "-", quantity: 0 };
    const avgQuantity = totalMaterials > 0 ? totalQuantity / totalMaterials : 0;

    return { totalMaterials, totalQuantity, topMaterial, avgQuantity };
  }, [allMaterials, topMaterials]);

  // Filter materials
  const filteredMaterials = useMemo(() => {
    if (!searchText) return allMaterials;
    const search = searchText.toLowerCase();
    return allMaterials.filter((p) => p.name?.toLowerCase().includes(search));
  }, [allMaterials, searchText]);

  // Chart data with colors
  const chartData = useMemo(() => {
    return topMaterials.map((material, index) => ({
      name: material.name?.length > 20 ? material.name.substring(0, 20) + "..." : material.name,
      fullName: material.name,
      quantity: material.quantity,
      fill: COLORS[index % COLORS.length],
    }));
  }, [topMaterials]);

  // DataGrid columns
  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "#",
        width: 70,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const rank = params.value;
          const isTop3 = rank <= 3;
          const colors = { 1: "#fbbf24", 2: "#9ca3af", 3: "#cd7f32" };
          return isTop3 ? (
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: colors[rank],
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              {rank}
            </Avatar>
          ) : (
            <Typography variant="body2" color="#6b7280" fontWeight={500}>
              {rank}
            </Typography>
          );
        },
      },
      {
        field: "name",
        headerName: t("Material Name"),
        flex: 1,
        minWidth: 250,
        renderCell: (params) => (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: COLORS[(params.row.rank - 1) % COLORS.length],
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {params.value?.charAt(0).toUpperCase() || "M"}
            </Avatar>
            <Typography variant="body2" fontWeight={500} color="#111827">
              {params.value || "-"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "quantity",
        headerName: t("Quantity Purchased"),
        width: 180,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Chip
            label={params.value.toLocaleString()}
            sx={{
              bgcolor: "#f3e8ff",
              color: "#8b5cf6",
              fontWeight: 600,
              minWidth: 70,
            }}
          />
        ),
      },
      {
        field: "percentage",
        headerName: t("Share"),
        width: 120,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => {
          const percentage = stats.totalQuantity > 0
            ? ((params.row.quantity / stats.totalQuantity) * 100).toFixed(1)
            : 0;
          return (
            <Typography variant="body2" color="#6b7280">
              {percentage}%
            </Typography>
          );
        },
      },
    ],
    [t, stats.totalQuantity]
  );

  const breadcrumbs = [
    { label: t("Views"), path: "/dashboard" },
    { label: t("Top Materials") },
  ];

  if (loading) {
    return (
      <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
        <Container maxWidth={false}>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth={false}>
        <MuiPageHeader
          title={t("Top Materials")}
          subtitle={t("Most purchased materials from providers")}
          breadcrumbs={breadcrumbs}
          action={
            <IconButton
              size="small"
              onClick={fetchData}
              disabled={loading}
              sx={{ color: "#6b7280" }}
              title={t("Refresh")}
            >
              <RefreshIcon />
            </IconButton>
          }
        />

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", width: 48, height: 48 }}>
                    <CategoryIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      {stats.totalMaterials}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Materials")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 48, height: 48 }}>
                    <StorefrontIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      {stats.totalQuantity.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Total Units")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 48, height: 48 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color="#111827">
                      {Math.round(stats.avgQuantity)}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      {t("Avg per Material")}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                bgcolor: "#f3e8ff",
                height: "100%",
                transition: "all 0.2s ease",
                "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.08)", transform: "translateY(-2px)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "#8b5cf6", color: "#fff", width: 48, height: 48 }}>
                    <EmojiEventsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#6b21a8" noWrap sx={{ maxWidth: 120 }}>
                      {stats.topMaterial.name}
                    </Typography>
                    <Typography variant="caption" color="#7c3aed">
                      {t("Top Material")}: {stats.topMaterial.quantity}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Chart Card */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#111827">
                {t("Top 10 Purchased Materials")}
              </Typography>
              <Typography variant="caption" color="#6b7280">
                {t("Based on provider orders quantity")}
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
            >
              <ToggleButton value="chart" sx={{ px: 2 }}>
                <BarChartIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
              <ToggleButton value="pie" sx={{ px: 2 }}>
                <PieChartIcon sx={{ fontSize: 18 }} />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <CardContent>
            {chartData.length > 0 ? (
              viewMode === "chart" ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                      formatter={(value) => [value.toLocaleString(), t("Quantity Purchased")]}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                    />
                    <Bar dataKey="quantity" radius={[8, 8, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="quantity"
                      nameKey="fullName"
                      cx="50%"
                      cy="50%"
                      outerRadius={150}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: "#6b7280" }}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [value.toLocaleString(), t("Quantity")]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )
            ) : (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography color="#6b7280">{t("No data available")}</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Materials Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid #f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="subtitle1" fontWeight={600} color="#111827">
                {t("All Materials")}
              </Typography>
              <Chip
                label={filteredMaterials.length}
                size="small"
                sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", fontWeight: 600 }}
              />
            </Stack>
            <TextField
              size="small"
              placeholder={t("Search materials...")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 280 },
                "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.875rem", bgcolor: "#f9fafb" },
              }}
            />
          </Box>

          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={filteredMaterials}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              disableColumnMenu
              rowHeight={56}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "rank", sort: "asc" }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  color: "#6b7280",
                  textTransform: "uppercase",
                },
                "& .MuiDataGrid-row": { "&:hover": { bgcolor: "#f9fafb" } },
                "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center" },
              }}
              localeText={{
                noRowsLabel: t("No materials found"),
                MuiTablePagination: { labelRowsPerPage: t("Rows per page") },
              }}
            />
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default MuiTopMaterials;
